import React, { useState } from "react";
import ButtonSuccess from "@ugg/shared/components/common/ButtonSuccess";
import { AuthInput } from "@ugg/shared/components/auth/AuthInput";
import type { useEmailLogin } from "@ugg/shared/hooks/use-email-login";

type LoginFormProps = React.PropsWithChildren<
  ReturnType<typeof useEmailLogin> & {
    submitOnEnter?: boolean;
    emailInputRef?: React.Ref<HTMLInputElement>;
    allowUsername?: boolean;
  }
>;

export const LoginForm = ({
  step,
  error,
  isLoading,
  submitCredentials,
  submitCode,
  resendCode,
  reset,
  captchaContainerRef,
  submitOnEnter = true,
  emailInputRef,
  allowUsername = false,
  children,
}: LoginFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const identifierLabel = allowUsername ? "Email or username" : "Email";

  const onSubmitCredentials = () => {
    const missingEmail = email.trim().length === 0 ? `Missing ${identifierLabel.toLowerCase()}` : "";
    const missingPassword = password.trim().length === 0 ? "Missing password" : "";
    setEmailError(missingEmail);
    setPasswordError(missingPassword);
    if (!missingEmail && !missingPassword) {
      submitCredentials(email, password);
    }
  };

  const errorMessage = error && <p className="mb-[8px] text-[14px] text-accent-red-450 font-[500]">{error}</p>;

  if (step === "code") {
    return (
      <>
        <div className="mb-[36px] text-white text-[30px] font-bold font-barlow">Enter the code sent to {email}.</div>
        <form onSubmit={(e) => e.preventDefault()} className="w-full text-left">
          {errorMessage}
          <AuthInput
            value={code}
            invalid={!!error}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Verification code"
            tabIndex={1000}
            autoFocus
          />
          <div ref={captchaContainerRef} className="mt-5" />
          <ButtonSuccess
            className="btn-blue h-[42px] !text-[14px] mt-5"
            label="Verify"
            isLoading={isLoading}
            onClick={() => submitCode(code)}
            bForceSubmitOnEnter={submitOnEnter}
          />
          <div className="mt-[20px] flex flex-col items-start gap-[8px]">
            <button type="button" className="text-[14px] text-accent-blue-400 font-bold underline" onClick={resendCode}>
              Resend code
            </button>
            <button
              type="button"
              className="text-[14px] text-accent-blue-400 font-bold underline"
              onClick={() => {
                setCode("");
                reset();
              }}
            >
              Use a different account
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <div className="text-white text-[30px] font-bold font-barlow">Welcome back!</div>
      <div className="mb-[36px] text-white text-[30px] font-bold font-barlow">Log in to continue.</div>
      <form onSubmit={(e) => e.preventDefault()} className="w-full text-left">
        {errorMessage}
        <AuthInput
          ref={emailInputRef}
          className="mb-[12px]"
          value={email}
          error={emailError}
          invalid={!!error}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={identifierLabel}
          type={allowUsername ? "text" : "email"}
          tabIndex={1000}
          autoFocus
        />
        <AuthInput
          value={password}
          error={passwordError}
          invalid={!!error}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          type="password"
          tabIndex={1000}
        />
        <div ref={captchaContainerRef} className="mt-5" />
        <ButtonSuccess
          className="btn-blue h-[42px] !text-[14px] mt-5"
          label="Login"
          isLoading={isLoading}
          onClick={onSubmitCredentials}
          bForceSubmitOnEnter={submitOnEnter}
        />
        {children}
      </form>
    </>
  );
};
